/**
 * The applicant-facing notes shown when a verification step falls back to another API.
 *
 * WHY THIS FILE EXISTS. These were written inline in all ten step runners — twenty separate
 * strings for the same two events, in five different wordings. The applicant hitting the same
 * transient failure was told "That check didn't go through. Retrying another way…" on PAN,
 * "…Retrying with another bank check…" on Bank, and "…Retrying…" on Face Match. Nobody chose
 * that; it is what happens when a message has ten homes.
 *
 * WHAT THESE SAY, AND WHAT THEY DELIBERATELY DO NOT.
 *
 * A fallback is a TECHNICAL failure — the provider was down or errored — that we are already
 * recovering from without the applicant doing anything. So the note's whole job is "keep
 * waiting, nothing is wrong with what you typed". It follows that:
 *
 *   • No blame and no alarm. "That check didn't go through" reads like the applicant's details
 *     were rejected, which is a different thing entirely and the thing they will assume.
 *   • No provider or method names. "another bank check", "Aadhaar OVSE" — these are internal
 *     product names that mean nothing to an applicant and reveal our vendor chain.
 *   • No provider reason text. Some of these used to append the gateway's own error string.
 *     On a technical failure that string is for us, not for someone holding their phone, and
 *     it is the opposite of short.
 */

/**
 * The note shown when a step moves to its next method.
 *
 * TWO THINGS THIS GETS RIGHT that every earlier version got wrong.
 *
 * 1. IT LEADS WITH WHAT HAPPENS NEXT, NOT WITH THE FAILURE. "That check didn't go through",
 *    "We could not check that just now" — all of them opened by telling the applicant
 *    something had gone wrong. From where they sit that reads as *their* details being
 *    rejected, which is both untrue and the most alarming reading available. The failure is
 *    ours and it is already handled; what they need is the way forward.
 *
 * 2. IT NAMES THE METHOD whenever there is a name fit to show. "Let's try with your Passport
 *    instead" tells the applicant exactly what is about to be asked of them; "trying another
 *    way" left them guessing why the screen had changed. `label` must be an APPLICANT-FACING
 *    name — "Passport", "a photo of your PAN card" — never the binding's `summary`, which
 *    holds internal product names like "Penny Drop - Basic".
 *
 * Some methods have no name worth showing: the four bank-account strategies are internal
 * plumbing over identical fields, so there is nothing to announce. Those pass no label and
 * get the shorter form.
 *
 * BOTH FORMS NAME THE BUTTON, because the runners do NOT auto-submit on fallback — they
 * switch method and stop. Any wording implying a retry was already running described
 * something that never happened, and left the applicant waiting on a screen that claimed to
 * be working while the only way forward was a button they had been told not to press.
 */
export function tryAnotherMethodNote(label?: string): string {
  return label
    ? `Let's try with ${label} instead — tap Continue when you're ready.`
    : "Let's try that once more — tap Continue when you're ready.";
}

/**
 * Every configured API failed technically. The step stays retryable, so this asks for the one
 * thing that might help rather than presenting a dead end.
 *
 * Does not name the check — the screen's own title says "PAN Verification" directly above it,
 * and repeating it is the kind of padding that made the old messages two sentences long.
 */
export const RETRY_EXHAUSTED_NOTE =
  "This check isn't responding right now. Tap Continue to try again.";

/**
 * The toast shown when a provider call fails technically.
 *
 * THIS REPLACED SHOWING THE PROVIDER'S OWN ERROR TEXT, which is the important part. Every
 * runner used to do `toast.error(extractError(resultJson) ?? 'Verification failed')`, so
 * whatever the gateway said went straight to the applicant's screen. In practice that meant
 * lines like:
 *
 *   "AWS Rekognition error: User: arn:aws:iam::339712798945:user/tejasm is not authorized
 *    to perform: rekognition:CompareFaces with an explicit deny in an identity-based policy"
 *
 * An applicant cannot act on that, and it should never have been in front of them: it names
 * our vendor, our AWS account number, and an IAM user. That is infrastructure detail leaking
 * out of a KYC flow to whoever happens to be holding the phone.
 *
 * NOTHING IS LOST FOR DIAGNOSIS. The provider's exact message is still stored verbatim in the
 * step's result JSON and still shown to the admin on the session detail page — which is where
 * a vendor error belongs, in front of someone who can act on it.
 */
export const CHECK_FAILED_TOAST = 'Something went wrong. Please try again.';
